import React, { useState, useRef, useEffect } from "react";
import { attachWork, detachWork, uploadTaskFile } from "../../../../api/hub/work";
import { supabase } from "../../../../lib/supabase";

// ─── Icons (inline SVG; lucide-react is not a project dependency) ───────────────
// Paths mirror lucide-react glyphs so the visual matches the tracker spec
// without adding a new dependency.
const PaperclipIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

// Generic file icon for non-image attachments (mirrors lucide-react's File
// glyph). Image types show their signed-URL thumbnail instead.
const FileIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// Trash icon — mirrors lucide-react's Trash2 glyph. Used by the per-row Remove
// button (confirm-on-second-click: "Remove?" → confirm).
const TrashIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

// Download icon — mirrors lucide-react's Download glyph (arrow down into a
// tray). Used by the per-row Download button (signed URL → save to disk
// instead of opening in a tab).
const DownloadIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// Check icon shown on an upload row once it succeeds.
const CheckIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Spinner shown on an upload row while it's in flight.
const SpinnerIcon = ({ className = "" }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ─── AttachmentList ─────────────────────────────────────────────────────────────
// Files section for the task slide-out (work-page-fixes §D). Lists attachments
// from fn_work_detail with Open (signed URL, file's own bucket) and Remove
// (confirm-on-second-click → fn_work_detach → conditional storage remove).
// The drop zone uploads to the existing task via uploadTaskFile then
// fn_work_attach, with per-file status (spinner → ✓ or error + Retry). After
// uploads settle, onTaskUpdated refreshes the detail.
const AttachmentList = ({ attachments, taskId, onTaskUpdated }) => {
  // ─── State ──────────────────────────────────────────────────────────────
  // signedUrls: pre-fetched signed URLs for storage-keyed attachments (private
  //   bucket) so thumbnails + Open resolve instantly. Keyed by storage path.
  // removedIds: attachments already deleted — hidden locally until the parent
  //   re-fetches fn_work_detail via onTaskUpdated.
  // removingId: the row currently armed for confirm-on-second-click Remove.
  // removeErrors / openErrors: per-row inline error text.
  // uploads: pending uploads from the drop zone. Each entry:
  //   { key, file, status, error, thumbUrl } (status: pending|uploading|done|failed).
  const [signedUrls, setSignedUrls] = useState({});
  const [removedIds, setRemovedIds] = useState({});
  const [removingId, setRemovingId] = useState(null);
  const [removeErrors, setRemoveErrors] = useState({});
  const [openErrors, setOpenErrors] = useState({});
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  // ─── Collapse file list (work-page-fixes Item 1) ──────────────────────
  // showAllFiles toggles between the first 5 attachments (default) and the
  // full list. Only completed/ready attachments (the `list` array below)
  // are subject to the 5-item cap — pending upload rows are rendered in a
  // separate block and are always visible. The header count always shows
  // the total `list.length`, never the sliced count.
  const [showAllFiles, setShowAllFiles] = useState(false);
  const fileInputRef = useRef(null);
  // Mirror of `uploads` for the unmount cleanup so it can revoke object URLs
  // without reading stale state. Updated every render (cheap).
  const uploadsRef = useRef([]);
  uploadsRef.current = uploads;

  // ─── List ────────────────────────────────────────────────────────────────
  // fn_work_detail attachments: { id, name, title, filename, type, content_type,
  // path, storage_path, bucket, url, at, size, size_bytes, can_remove, source }.
  // Rows whose id is in removedIds are hidden locally until the parent re-fetches
  // fn_work_detail via onTaskUpdated.
  const list = (Array.isArray(attachments) ? attachments : []).filter(
    (a) => a && !removedIds[a.id]
  );

  // ─── Signed URLs (private bucket) ────────────────────────────────────────
  // Pre-fetch one signed URL per storage ref on mount and whenever the set
  // changes (e.g. after an upload re-fetches detail). Each ref uses the file's
  // OWN bucket (never hardcoded); the "work-attachments" fallback only covers
  // rows where fn_work_detail omits the bucket field. Direct `url` attachments
  // (ClickUp links) skip signing — they're already clickable.
  const storageRefs = list
    .filter((a) => !a.url && (a.path || a.storage_path))
    .map((a) => ({ bucket: a.bucket || "work-attachments", key: a.path || a.storage_path }));
  const refsKey = JSON.stringify(storageRefs);
  useEffect(() => {
    if (!storageRefs.length) { setSignedUrls({}); return; }
    let cancelled = false;
    const refs = [...storageRefs];
    Promise.all(
      refs.map(async (r) => {
        const { data, error } = await supabase.storage
          .from(r.bucket)
          .createSignedUrl(r.key, 3600);
        return [r.key, error ? null : data?.signedUrl || null];
      })
    ).then((results) => {
      if (cancelled) return;
      const next = {};
      for (const [k, url] of results) if (url) next[k] = url;
      setSignedUrls(next);
    }).catch(() => { if (!cancelled) setSignedUrls({}); });
    return () => { cancelled = true; };
  }, [refsKey]);

  // Revoke pending upload thumbnail object URLs on unmount so they don't leak.
  useEffect(() => () => {
    uploadsRef.current.forEach((u) => { if (u.thumbUrl) URL.revokeObjectURL(u.thumbUrl); });
  }, []);

  // ─── Open (signed URL → new tab) ──────────────────────────────────────────
  // Uses the file's own bucket. Prefers a pre-fetched signed URL; if none is
  // ready yet, generates one on click. Direct `url` attachments open as-is.
  // On failure the error text is shown inline beneath the row (work-page-fixes §D).
  const handleOpen = async (a) => {
    const rowId = a.id || a.path || a.storage_path;
    setOpenErrors((prev) => { const next = { ...prev }; delete next[rowId]; return next; });
    if (a.url) { window.open(a.url, "_blank", "noopener,noreferrer"); return; }
    const storageKey = a.path || a.storage_path;
    const bucket = a.bucket || "work-attachments";
    let url = storageKey ? signedUrls[storageKey] || null : null;
    if (!url && storageKey) {
      try {
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrl(storageKey, 3600);
        if (error) throw error;
        url = data?.signedUrl || null;
      } catch (e) {
        setOpenErrors((prev) => ({ ...prev, [rowId]: e?.message || "Could not open file" }));
        return;
      }
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else setOpenErrors((prev) => ({ ...prev, [rowId]: "No file path available" }));
  };

  // ─── Download (signed URL → save to disk) ─────────────────────────────────
  // Same bucket/path as handleOpen, but signs a FRESH URL with the `download`
  // option so Supabase sets Content-Disposition: attachment (forces save
  // instead of display). The pre-fetched signedUrls lack that header, so they
  // are never reused here. Direct `url` attachments (ClickUp links) fall back
  // to an anchor with the `download` attribute. Errors surface inline beneath
  // the row via openErrors, matching handleOpen.
  const handleDownload = async (a) => {
    const rowId = a.id || a.path || a.storage_path;
    setOpenErrors((prev) => { const next = { ...prev }; delete next[rowId]; return next; });
    const filename = a.name || a.filename || a.title || "file";
    const triggerDownload = (url) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    if (a.url) { triggerDownload(a.url); return; }
    const storageKey = a.path || a.storage_path;
    if (!storageKey) {
      setOpenErrors((prev) => ({ ...prev, [rowId]: "No file path available" }));
      return;
    }
    const bucket = a.bucket || "work-attachments";
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(storageKey, 3600, { download: filename });
      if (error) throw error;
      const url = data?.signedUrl || null;
      if (!url) throw new Error("No signed URL returned");
      triggerDownload(url);
    } catch (e) {
      setOpenErrors((prev) => ({ ...prev, [rowId]: e?.message || "Could not download file" }));
    }
  };

  // ─── Remove (confirm-on-second-click → fn_work_detach) ────────────────────
  // First click arms confirm ("Remove?"); second click calls fn_work_detach.
  // When the response has remove_file true, the Storage object is also removed
  // using the backend's bucket/path. On success the row hides locally
  // (removedIds) and onTaskUpdated refreshes the server-side list. On error the
  // message shows inline and the file stays.
  const handleRemoveClick = async (a) => {
    const rowId = a.id || a.storage_path || a.path;
    if (removingId !== rowId) {
      setRemovingId(rowId);
      setRemoveErrors((prev) => { const next = { ...prev }; delete next[rowId]; return next; });
      return;
    }
    setRemovingId(null);
    if (!a.id) {
      setRemoveErrors((prev) => ({ ...prev, [rowId]: "No attachment id — cannot delete this file." }));
      return;
    }
    try {
      const resp = await detachWork(a.id);
      const detached = resp?.data;
      if (detached?.remove_file) {
        const { error: removeError } = await supabase.storage
          .from(detached.bucket)
          .remove([detached.path]);
        if (removeError) throw removeError;
      }
      setRemovedIds((prev) => ({ ...prev, [a.id]: true }));
      setRemoveErrors((prev) => { const next = { ...prev }; delete next[rowId]; return next; });
      onTaskUpdated ? onTaskUpdated() : window.location.reload();
    } catch (e) {
      setRemoveErrors((prev) => ({ ...prev, [rowId]: e?.message || "Delete failed" }));
    }
  };

  // ─── Upload to existing task (drop zone) ───────────────────────
  // Mirrors the new-task form (work-page-fixes §C): upload to the
  // work-attachments bucket first (uploadTaskFile), then call fn_work_attach
  // only after the upload succeeds. Per-file status: spinner → ✓ or error +
  // Retry. After uploads settle, onTaskUpdated refreshes the detail so the new
  // rows appear; "done" entries are then dropped from the pending list.
  const makeEntry = (file) => ({
    key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
    file,
    status: "pending",
    error: null,
    thumbUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
  });

  const uploadOne = async (entry) => {
    if (!taskId) return false;
    const safeName = entry.file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${taskId}/${Date.now()}-${safeName}`;
    setUploads((prev) => prev.map((u) => (u.key === entry.key ? { ...u, status: "uploading", error: null } : u)));
    try {
      await uploadTaskFile(path, entry.file);
      await attachWork(taskId, {
        filename: entry.file.name,
        storage_path: path,
        content_type: entry.file.type,
        size_bytes: entry.file.size,
        title: entry.file.name,
      });
      setUploads((prev) => prev.map((u) => (u.key === entry.key ? { ...u, status: "done" } : u)));
      return true;
    } catch (e) {
      setUploads((prev) => prev.map((u) => (u.key === entry.key ? { ...u, status: "failed", error: e?.message || "Upload failed" } : u)));
      return false;
    }
  };

  const settleUploads = () => {
    onTaskUpdated ? onTaskUpdated() : void 0;
    setUploads((prev) => {
      prev.forEach((u) => { if (u.status === "done" && u.thumbUrl) URL.revokeObjectURL(u.thumbUrl); });
      return prev.filter((u) => u.status !== "done");
    });
  };

  const handleAddFiles = (newFiles) => {
    const arr = Array.from(newFiles || []);
    if (!arr.length) return;
    const entries = arr.map(makeEntry);
    setUploads((prev) => [...prev, ...entries]);
    Promise.all(entries.map((e) => uploadOne(e))).then(settleUploads);
  };

  const retryUpload = (entry) => {
    uploadOne(entry).then(() => settleUploads());
  };

  const removeUpload = (key) => {
    setUploads((prev) => {
      const t = prev.find((u) => u.key === key);
      if (t?.thumbUrl) URL.revokeObjectURL(t.thumbUrl);
      return prev.filter((u) => u.key !== key);
    });
  };

  const isImage = (a) => (a.type || a.content_type || "").startsWith("image/");

  // Keep the section mounted whenever there's something to show OR attach to,
  // so the drop zone stays reachable with no files.
  if (list.length === 0 && !taskId && uploads.length === 0) return null;

  return (
    <div
      className="px-5 py-3 border-b border-gray-200 dark:border-gray-800"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) handleAddFiles(e.dataTransfer.files); }}
    >
      {/* ─── Header: "Files ({count})" ──────────────────────── */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-neutral-300">Files</h4>
          <span className="text-xs text-neutral-500 bg-neutral-800 rounded-full px-1.5 py-0.5">
            {list.length}
          </span>
        </div>
      </div>

      {/* ─── File rows: thumbnail/icon, filename (click → Open),
          Remove button (only when can_remove). Always visible. */}
      {list.length > 0 && (
        <div className="space-y-1">
          {(showAllFiles ? list : list.slice(0, 5)).map((a, i) => {
            const rowId = a.id || a.storage_path || a.path || i;
            const storageKey = a.path || a.storage_path;
            const href = a.url || (storageKey ? signedUrls[storageKey] || null : null);
            const label = a.name || a.title || a.filename || "File";
            const armed = removingId === rowId;
            const openErr = openErrors[rowId];
            const rmErr = removeErrors[rowId];
            return (
              <div key={rowId}>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-800/50">
                  {isImage(a) && href ? (
                    <img src={href} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                  ) : isImage(a) ? (
                    <div className="w-8 h-8 rounded bg-neutral-700 flex-shrink-0" />
                  ) : (
                    <FileIcon className="w-4 h-4 flex-shrink-0 text-neutral-500" />
                  )}
                  <button
                    type="button"
                    onClick={() => handleOpen(a)}
                    className="flex-1 text-sm text-neutral-300 hover:text-neutral-100 overflow-hidden text-ellipsis whitespace-nowrap text-left cursor-pointer"
                    title={label}
                  >
                    {label}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDownload(a)}
                    className="flex-shrink-0 text-xs font-medium text-neutral-500 hover:text-blue-400"
                    title="Download"
                    aria-label="Download"
                  >
                    <DownloadIcon className="w-3.5 h-3.5" />
                  </button>
                  {a.can_remove && (
                    <button
                      type="button"
                      onClick={() => handleRemoveClick(a)}
                      className={`flex-shrink-0 text-xs font-medium ${armed ? "text-red-400" : "text-neutral-500 hover:text-red-400"}`}
                      title={armed ? "Click again to confirm" : "Remove"}
                      aria-label={armed ? "Confirm remove file" : "Remove"}
                    >
                      {armed ? "Remove?" : <TrashIcon className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
                {openErr && <p className="px-3 pb-1 text-xs text-red-400">{openErr}</p>}
                {rmErr && <p className="px-3 pb-1 text-xs text-red-400">{rmErr}</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Show all / Show less toggle (work-page-fixes Item 1) ───────────
          Appears only when there are more than 5 completed attachments.
          Collapsed → "Show all {N} files" expands to the full list; expanded
          → "Show less" collapses back to the first 5. Styled as a subtle
          text link (muted, hover underline) consistent with the section. */}
      {list.length > 5 && (
        <button
          type="button"
          onClick={() => setShowAllFiles((v) => !v)}
          className="mt-1 text-xs text-neutral-500 hover:text-neutral-300 hover:underline cursor-pointer"
        >
          {showAllFiles ? "Show less" : `Show all ${list.length} files`}
        </button>
      )}

      {/* ─── Pending uploads (per-file status: spinner → ✓ / error + Retry) ─── */}
      {uploads.length > 0 && (
        <div className="space-y-1 mb-2">
          {uploads.map((u) => (
            <div key={u.key} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-800/50">
              {u.thumbUrl ? (
                <img src={u.thumbUrl} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
              ) : (
                <FileIcon className="w-4 h-4 flex-shrink-0 text-neutral-500" />
              )}
              <span
                className="flex-1 text-sm text-neutral-300 overflow-hidden text-ellipsis whitespace-nowrap"
                title={u.file.name}
              >
                {u.file.name}
              </span>
              {u.status === "uploading" && <SpinnerIcon className="w-4 h-4 flex-shrink-0 text-neutral-400" />}
              {u.status === "done" && <CheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-500" />}
              {u.status === "failed" && (
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-red-400" title={u.error}>Failed</span>
                  <button
                    type="button"
                    onClick={() => retryUpload(u)}
                    className="text-xs text-blue-500 hover:text-blue-400"
                  >
                    Retry
                  </button>
                </span>
              )}
              {u.status === "pending" && (
                <button
                  type="button"
                  onClick={() => removeUpload(u.key)}
                  className="flex-shrink-0 text-neutral-500 hover:text-red-400 text-xs"
                  aria-label="Cancel upload"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ─── Drop zone + hidden input (existing task only) ───────────────
          Dashed-border container, always visible below the file rows. Click
          or drop triggers the hidden file input (multiple). The outer
          container carries the drag/drop handlers so dropping on the panel
          always works. */}
      {taskId && (
        <div
          className={`mt-6 flex items-center justify-center gap-2 rounded-lg border border-dashed p-3 text-sm cursor-pointer transition-colors ${
            dragOver ? "border-blue-500 bg-blue-500/10 text-blue-300" : "border-neutral-600 hover:border-neutral-400 text-neutral-400 hover:bg-neutral-800/50"
          }`}
          onClick={() => fileInputRef.current?.click()}
        >
          <PaperclipIcon className="w-4 h-4 flex-shrink-0" />
          <span>
            {list.length === 0 && uploads.length === 0
              ? "No files yet — drop one here."
              : "Drop files here or click to browse"}
          </span>
        </div>
      )}
      {taskId && (
        <input
          type="file"
          ref={fileInputRef}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) handleAddFiles(e.target.files);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />
      )}
    </div>
  );
};

export default AttachmentList;
